import { useEffect, useRef, useState, type ReactNode } from 'react';
import { darkTheme, FONT } from '../../theme/tokens';

interface PopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'top-start' | 'top-end';
}

export function Popover({ trigger, children, align = 'top-start' }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const t = darkTheme;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        {trigger}
      </div>
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'absolute',
            bottom: '100%',
            marginBottom: 8,
            ...(align === 'top-end' ? { right: 0 } : { left: 0 }),
            minWidth: 200,
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            padding: 4,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface MenuItemProps {
  onClick: () => void;
  children: ReactNode;
  variant?: 'default' | 'danger';
}

export function MenuItem({ onClick, children, variant = 'default' }: MenuItemProps) {
  const t = darkTheme;
  const color = variant === 'danger' ? t.red : t.text;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        background: 'transparent',
        border: 'none',
        color,
        fontSize: 13,
        fontFamily: FONT,
        cursor: 'pointer',
        borderRadius: 6,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = t.surfaceAlt;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

export function MenuDivider() {
  const t = darkTheme;
  return <div style={{ height: 1, background: t.border, margin: '4px 0' }} />;
}
